import type { TemplatePrintOptions } from 'src/app/models/template.model';

export const CHAIN_OF_CUSTODY_DEFAULT_PRINT_OPTIONS: TemplatePrintOptions = {
  pageSize: 'A4',
  pageOrientation: 'landscape',
  margins: '0mm',
  scale: 1
};

export const CHAIN_OF_CUSTODY_PAGE_SIZES = ['A3', 'A4', 'A5', 'Letter', 'Legal', 'Tabloid'];

export const CHAIN_OF_CUSTODY_SAMPLE_TABLE = `<style>
.coc-samples { border-collapse: collapse; width: 100%; }
.coc-page .coc-th { background-color: #ebf0fa !important; border: 1px solid #999; padding: 4px; text-align: center; vertical-align: top; font-weight: bold; }
.coc-page .coc-td { border: 1px solid #999; padding: 5px; text-align: center; vertical-align: middle; }
.coc-samples tr { page-break-inside: avoid; }
.coc-row { height: 50px; }
.coc-barcode { height: 40px; }
</style>
<table class="coc-samples">
<thead>
<tr>
<th class="coc-th">[[labels.sampleNumber]]</th>
<th class="coc-th" data-tpl-if="!lab.isLims">[[alternateSampleLabel]]</th>
<th class="coc-th">[[labels.agentName]]</th>
<th class="coc-th">[[labels.agentGroup]]</th>
<th class="coc-th">[[labels.samplingDate]]</th>
<th class="coc-th">[[labels.sampleVolume]]</th>
<th class="coc-th">[[labels.totalTime]]</th>
<th class="coc-th">[[labels.flowRate]]</th>
<th class="coc-th">[[labels.methodReference]]</th>
</tr>
</thead>
<tbody>
<tr class="coc-row" data-tpl-each="samples">
<td class="coc-td"><div>[[this.sampleNumber]]</div><figure class="image" data-tpl-if="this.barcodeImg"><img class="coc-barcode" src="[[this.barcodeImg]]"></figure></td>
<td class="coc-td" data-tpl-if="!lab.isLims">[[this.alternateSampleNumber]]</td>
<td class="coc-td">[[this.agentNames]]</td>
<td class="coc-td">[[this.agentGroupName]]</td>
<td class="coc-td">[[this.samplingDate]]</td>
<td class="coc-td">[[this.volumeLiters]]</td>
<td class="coc-td">[[this.runTimeMinutes]]</td>
<td class="coc-td">[[this.flowRateFormatted]]</td>
<td class="coc-td">[[this.analyticalMethodName]]</td>
</tr>
</tbody>
</table>`;

export const CHAIN_OF_CUSTODY_DEFAULT_LAYOUT = `<style>
.coc-page { box-sizing: border-box; padding: 10mm; font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: #000; }
.coc-page table { border-collapse: collapse; width: 100%; }
.coc-page figure { margin: 0; }
.coc-page figure.table { display: block; float: none; width: auto; max-width: none; height: auto; margin: 0; padding: 0; overflow: visible; font: inherit; color: inherit; }
.coc-page figure.table > table { width: 100%; height: auto; margin: 0; }
.coc-page :where(figure.table) :is(td, th), .coc-page :where(figure.table) thead th { font-size: inherit; line-height: inherit; color: inherit; padding: 4px 8px; border: 1px solid #bfbfbf; vertical-align: top; }
.coc-page :where(figure.table) th, .coc-page :where(figure.table) thead th { font-weight: 600; }
.coc-page p { margin: 0; }
.coc-head { position: relative; min-height: 44px; margin-bottom: 8px; }
.coc-logo { position: absolute; left: 0; top: 0; width: 44px; height: 44px; }
.coc-title { text-align: center; font-size: 14px; font-weight: bold; line-height: 30px; }
.coc-page .coc-top-left { width: 140px; vertical-align: top; }
.coc-page .coc-top-right { vertical-align: top; padding-left: 20px; }
.coc-box { border: 1px solid #b4b4b4; border-radius: 4px; padding: 6px 10px; box-sizing: border-box; }
.coc-account { margin-bottom: 5px; }
.coc-account-number { text-align: right; }
.coc-page .coc-info-cell { width: 50%; vertical-align: top; }
.coc-info-box { min-height: 110px; font-size: 9px; line-height: 1.5; }
.coc-gap { height: 10px; }
.coc-tall { height: 30px; }
.coc-page .coc-site-cell { width: 25%; vertical-align: top; }
.coc-page .coc-comment-cell { width: 50%; vertical-align: top; }
.coc-comment-box { min-height: 70px; }
.coc-page .coc-sign td, .coc-page .coc-sign th { height: 45px; padding: 3px; }
.coc-signature { white-space: pre-line; }
.coc-footer { text-align: right; font-size: 9px; margin-top: 15px; padding-right: 10px; }
</style>
<div class="coc-page">
<div class="coc-head">
<figure class="image" data-tpl-if="organization.tenantLogoImg"><img class="coc-logo" src="[[organization.tenantLogoImg]]"></figure>
<div class="coc-title">[[labels.chainOfCustody]]</div>
</div>
<table>
<tr>
<td class="coc-top-left">
<table>
<tr><th class="coc-th">[[labels.turnAroundTime]]</th><th class="coc-th">[[labels.surcharge]]</th></tr>
<tr><td class="coc-td coc-tall">[[requisition.turnAroundTime]]</td><td class="coc-td coc-tall">[[requisition.surcharge]]</td></tr>
</table>
</td>
<td class="coc-top-right">
<div class="coc-box coc-account">
<table>
<tr>
<td>[[requisition.accountLabel]]: [[requisition.accountValue]]</td>
<td class="coc-account-number">[[labels.labRequisitionNumber]]: [[requisition.labRequisitionNumber]]</td>
</tr>
</table>
</div>
<table>
<tr>
<td class="coc-info-cell">
<div class="coc-box coc-info-box">
<div>[[labels.reportTo]]: [[requisition.reportTo]]</div>
<div>[[labels.companyName]]: [[requisition.reportToCompanyName]]</div>
<div>[[labels.address]]: [[requisition.reportToAddress1]]</div>
<div>[[labels.cityStateZip]]: [[requisition.reportToCityStateZip]]</div>
<div>[[labels.phoneNumber]]: [[requisition.reportToPhoneNumber]]</div>
<div>[[labels.cellNumber]]: [[requisition.reportToCellNumber]]</div>
<div>[[labels.emailReportTo]]: [[requisition.reportToEmail]]</div>
<div>[[labels.emailEdd]]: [[requisition.emailEdd]]</div>
<div>[[labels.reportToComments]]: [[requisition.reportToComments]]</div>
</div>
</td>
<td class="coc-info-cell">
<div class="coc-box coc-info-box">
<div>[[labels.invoiceTo]]: [[requisition.invoiceTo]]</div>
<div>[[labels.companyName]]: [[requisition.invoiceCompanyName]]</div>
<div>[[labels.address]]: [[requisition.invoiceAddress1]]</div>
<div>[[labels.phoneNumber]]: [[requisition.invoicePhoneNumber]]</div>
<div>[[labels.emailInvoiceTo]]: [[requisition.invoiceEmail]]</div>
<div>[[labels.po]]: [[requisition.invoicePoNumber]]</div>
<div>[[labels.paymentMethod]]: [[requisition.invoicePaymentInfo]]</div>
<div>[[labels.invoiceToComments]]: [[requisition.invoiceComments]]</div>
</div>
</td>
</tr>
</table>
</td>
</tr>
</table>
<div class="coc-gap"></div>
<div class="coc-box">
<table>
<tr>
<td class="coc-site-cell">[[labels.siteName]]: [[summary.siteNames]]</td>
<td class="coc-site-cell">[[labels.survey]]: [[summary.surveyNames]]</td>
<td class="coc-site-cell">[[labels.sampledBy]]: [[summary.sampledBy]]</td>
<td class="coc-site-cell">[[labels.jurisdiction]]: [[summary.jurisdictions]]</td>
</tr>
</table>
</div>
<div class="coc-gap"></div>
<table>
<tr>
<td class="coc-comment-cell">
<div class="coc-box coc-comment-box">
<div>[[labels.generalComments]]:</div>
<div>[[requisition.generalComments]]</div>
</div>
</td>
<td class="coc-comment-cell">
<div class="coc-box coc-comment-box">
<div>[[labels.industryDescription]]:</div>
<div>[[requisition.industryProcessInterferenceDescription]]</div>
</div>
</td>
</tr>
</table>
<div class="coc-gap"></div>
${CHAIN_OF_CUSTODY_SAMPLE_TABLE}
<div class="coc-gap"></div>
<table class="coc-sign">
<thead>
<tr>
<th class="coc-th"></th>
<th class="coc-th">[[labels.printNameSignature]]</th>
<th class="coc-th"></th>
<th class="coc-th"></th>
<th class="coc-th"></th>
<th class="coc-th">[[labels.printNameSignature]]</th>
<th class="coc-th"></th>
<th class="coc-th"></th>
</tr>
</thead>
<tbody>
<tr>
<td class="coc-td">[[labels.chainOfCustody]]</td>
<td class="coc-td"></td>
<td class="coc-td">[[labels.date]]</td>
<td class="coc-td">[[labels.time]]</td>
<td class="coc-td"></td>
<td class="coc-td"></td>
<td class="coc-td">[[labels.date]]</td>
<td class="coc-td">[[labels.time]]</td>
</tr>
<tr>
<td class="coc-td">[[labels.relinquishedBy]]:</td>
<td class="coc-td coc-signature">[[signature.relinquishedBy]]</td>
<td class="coc-td">[[signature.date]]</td>
<td class="coc-td">[[signature.time]]</td>
<td class="coc-td">[[labels.receivedBy]]:</td>
<td class="coc-td"></td>
<td class="coc-td"></td>
<td class="coc-td"></td>
</tr>
<tr>
<td class="coc-td">[[labels.relinquishedBy]]:</td>
<td class="coc-td"></td>
<td class="coc-td"></td>
<td class="coc-td"></td>
<td class="coc-td">[[labels.receivedBy]]:</td>
<td class="coc-td"></td>
<td class="coc-td"></td>
<td class="coc-td"></td>
</tr>
</tbody>
</table>
<div class="coc-footer">[[lab.addressLine]]</div>
</div>`;
